/**
 * Oracle JSON-path extraction from cursor context.
 *
 * Given the buffer text and a cursor offset inside a comparison RHS string
 * literal (`WHERE JSON_VALUE(metadata, '$.status') = '|'`), detects whether
 * the LHS of the comparison is an Oracle `JSON_VALUE(...)` scalar extract
 * and returns the normalized canonical JSONPath (`$.status`) so the
 * enum-value resolver can look up path-keyed hints.
 *
 * Supported Oracle JSON path forms (v1):
 *   JSON_VALUE(col, '$.key')                  → canonical `$.key`
 *   JSON_VALUE(col, '$.a.b.c')                → canonical `$.a.b.c`
 *   JSON_VALUE(col, '$.items[2].name')        → canonical `$.items[2].name`
 *   JSON_VALUE(t.col, '$.status')             → canonical `$.status`  (alias-qualified)
 *   JSON_VALUE("col", '$.status')             → canonical `$.status`  (double-quoted col)
 *
 * Intentionally NOT supported in v1 (follow-up slices):
 *   - Dot-notation chains: `SELECT t.metadata.status` — Oracle's column-access
 *     syntax on JSON columns. Per MERGE-2 handover §5.3 the extractor focuses
 *     on `JSON_VALUE(...)`; resolver-layer alias resolution may handle the
 *     dot-notation form separately.
 *   - `JSON_QUERY(...)` (returns a JSON fragment, not a scalar — not useful
 *     for enum-value matching).
 *   - `JSON_EXISTS(...)` / Oracle 21c+ filter expressions inside the path.
 *
 * Pure text scanner — no tree-sitter dependency. Mirrors the backward-scan
 * pattern of `extractJsonPathFromCursor.pgsql.ts`.
 */

import { normalizeJsonPath } from '@/features/workspace/common/utils/jsonFilterSql';
import {
  skipValueOperand,
  COMPARISON_OPERATORS,
  endsWithAt,
} from '../context/comparisonScanHelpers';

export type ExtractJsonPathResult = {
  /** Already passed through normalizeJsonPath. */
  readonly canonicalPath: string;
  /** The column name (unqualified, case-preserved) from the LHS. */
  readonly column: string;
};

/**
 * Returns the normalized JSON path when the cursor sits at the comparison
 * RHS of an Oracle `JSON_VALUE(...)` expression, or `null` otherwise.
 *
 * `column` is the column name (case-folded by the resolver per Oracle's
 * UPPERCASE-default rule) from the resolved table-column binding — used
 * to verify the extract expression targets the same column so we don't
 * offer completions for the wrong column.
 */
export function extractJsonPathFromCursorOracle(args: {
  readonly text: string;
  readonly cursorOffset: number;
  /** Omit to accept whichever column the extract expression names — the
   *  scan itself never depends on it (the JSON-chain enum fallback scans
   *  once, then matches the returned `column` against the snapshot). */
  readonly column?: string;
}): ExtractJsonPathResult | null {
  const { text, cursorOffset, column } = args;
  if (cursorOffset < 0 || cursorOffset > text.length) return null;

  // Walk backward from the cursor past the in-progress string literal to land
  // on the comparison operator boundary (exclusive end of LHS).
  const opBoundary = findComparisonOpEndBefore(text, cursorOffset);
  if (opBoundary === null) return null;

  // The token immediately before the comparison operator should be the closing
  // `)` of the `JSON_VALUE(...)` call. Skip whitespace.
  let i = opBoundary - 1;
  while (i >= 0 && /\s/.test(text[i]!)) i--;
  if (i < 0 || text[i] !== ')') return null;

  // Parse the JSON_VALUE call ending at this `)`.
  const extracted = parseJsonValueCallEndingAt(text, i);
  if (!extracted) return null;

  // Verify the column matches (case-insensitive — Oracle resolves bare
  // identifiers to UPPERCASE; the resolver passes the snapshot column name
  // which is already UPPERCASE for unquoted-defined columns).
  if (column !== undefined && extracted.column.toLowerCase() !== column.toLowerCase()) return null;

  return {
    canonicalPath: extracted.canonicalPath,
    column: extracted.column,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Comparison operator boundary scan
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Walk back from `offset` over the optional in-progress RHS string literal,
 * using `skipValueOperand` (which handles all the SQL string-scanning edge
 * cases) and return the exclusive-end index of the LHS expression
 * (i.e., `opBoundary` where `text.slice(0, opBoundary)` ends with the
 * comparison operator). Returns `null` when no recognized comparison operator
 * precedes the cursor's operand position.
 */
function findComparisonOpEndBefore(text: string, offset: number): number | null {
  const i = skipValueOperand(text, offset - 1);
  if (i < 0) return null;

  // `i` is the index of the last char of the operator (exclusive end = i+1).
  const opEnd = i + 1;
  for (const op of COMPARISON_OPERATORS) {
    if (endsWithAt(text, opEnd, op)) {
      return opEnd - op.length; // index of first char of the LHS (exclusive end)
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// JSON_VALUE call parser
// ─────────────────────────────────────────────────────────────────────────────

type ExtractResult = { column: string; canonicalPath: string };

/**
 * Parse `JSON_VALUE(<col>, '<path>')` whose closing `)` sits at `end`.
 * Returns null when the pattern isn't recognized.
 *
 * Walks back from the `)` to find the matching `(`, then matches the
 * function-name identifier preceding it. The two argument positions
 * inside the parens are matched leniently — we accept whitespace and
 * skip over optional RETURNING / ERROR / DEFAULT clauses by only
 * looking for `<col-ref> , '<path-literal>'` as the two leading args.
 */
function parseJsonValueCallEndingAt(text: string, end: number): ExtractResult | null {
  if (text[end] !== ')') return null;

  // Find the matching open paren.
  const openIdx = findMatchingOpenParen(text, end);
  if (openIdx < 0) return null;

  // The function-name identifier ends just before openIdx (skipping ws).
  let j = openIdx - 1;
  while (j >= 0 && /\s/.test(text[j]!)) j--;
  const fnNameEnd = j + 1;
  while (j >= 0 && /[A-Za-z0-9_]/.test(text[j]!)) j--;
  const fnName = text.slice(j + 1, fnNameEnd).toUpperCase();
  if (fnName !== 'JSON_VALUE') return null;

  // Parse the two leading arguments inside the parens: `<col-ref> , '<path>'`.
  // We start just after the opening paren and walk forward, but for our
  // simple shape a forward scan is easiest.
  return parseJsonValueArgs(text, openIdx + 1, end);
}

/**
 * Find the index of the `(` matching the `)` at `end`. Returns -1 when no
 * matching paren is found. Treats `(`/`)` inside SQL string literals as
 * non-paren characters.
 */
function findMatchingOpenParen(text: string, end: number): number {
  let depth = 1;
  let i = end - 1;
  while (i >= 0) {
    const ch = text[i]!;
    if (ch === "'") {
      // Skip back past a string literal — find the matching opening quote.
      const lit = readQuotedStringEndingAt(text, i);
      if (!lit) return -1;
      i = lit.openIdx - 1;
      continue;
    }
    if (ch === ')') depth++;
    else if (ch === '(') {
      depth--;
      if (depth === 0) return i;
    }
    i--;
  }
  return -1;
}

/**
 * Forward-parse `<col-ref> , '<path>'` between `start` (just after `(`)
 * and `end` (the closing `)`). Returns the column name (possibly
 * dot-qualified — we keep only the last segment) and the canonical path.
 */
function parseJsonValueArgs(text: string, start: number, end: number): ExtractResult | null {
  // Read the column-ref segment up to the top-level comma.
  let i = start;
  while (i < end && /\s/.test(text[i]!)) i++;
  const colStart = i;
  let colEnd = -1;
  while (i < end) {
    const ch = text[i]!;
    if (ch === ',') {
      colEnd = i;
      break;
    }
    if (ch === "'") {
      // String literal inside the column ref — unexpected, but skip safely.
      const lit = readQuotedStringStartingAt(text, i);
      if (!lit) return null;
      i = lit.closeIdx + 1;
      continue;
    }
    i++;
  }
  if (colEnd < 0) return null;

  const colRefRaw = text.slice(colStart, colEnd).trim();
  if (colRefRaw === '') return null;
  const column = extractTailIdent(colRefRaw);
  if (!column) return null;

  // Now read the path argument. Skip whitespace after the comma.
  i = colEnd + 1;
  while (i < end && /\s/.test(text[i]!)) i++;
  if (i >= end || text[i] !== "'") return null;
  const lit = readQuotedStringStartingAt(text, i);
  if (!lit) return null;
  const rawPath = lit.content;

  return { column, canonicalPath: normalizeJsonPath(rawPath) };
}

/**
 * Extract the trailing identifier from a (possibly dot-qualified) column ref
 * like `t.col`, `"Schema"."Table"."Col"`, or `metadata`. Returns the
 * unquoted last segment, or `null` when no identifier is found.
 */
function extractTailIdent(colRefRaw: string): string | null {
  // Walk back from the end, collecting the last identifier segment.
  let i = colRefRaw.length - 1;
  while (i >= 0 && /\s/.test(colRefRaw[i]!)) i--;
  if (i < 0) return null;

  if (colRefRaw[i] === '"') {
    // Double-quoted identifier: walk back to the opening quote (handling
    // doubled-`""` escapes by counting consecutive quote runs).
    let j = i - 1;
    while (j >= 0) {
      if (colRefRaw[j] === '"') {
        // Doubled-quote escape?
        if (j > 0 && colRefRaw[j - 1] === '"') {
          j -= 2;
          continue;
        }
        // Opening quote.
        return colRefRaw.slice(j + 1, i).replace(/""/g, '"');
      }
      j--;
    }
    return null;
  }

  // Bare identifier: collect [A-Za-z0-9_$#] chars walking back.
  const idEnd = i + 1;
  while (i >= 0 && /[A-Za-z0-9_$#]/.test(colRefRaw[i]!)) i--;
  const id = colRefRaw.slice(i + 1, idEnd);
  if (id.length === 0 || /^\d/.test(id)) return null;
  return id;
}

// ─────────────────────────────────────────────────────────────────────────────
// String literal helpers
// ─────────────────────────────────────────────────────────────────────────────

type QuotedString = {
  content: string;
  /** Index of the opening `'`. */
  openIdx: number;
};

function readQuotedStringEndingAt(text: string, end: number): QuotedString | null {
  if (text[end] !== "'") return null;
  let i = end - 1;
  let content = '';
  while (i >= 0) {
    const ch = text[i]!;
    if (ch === "'") {
      if (i > 0 && text[i - 1] === "'") {
        content = "'" + content;
        i -= 2;
        continue;
      }
      return { content, openIdx: i };
    }
    if (ch === '\n') return null;
    content = ch + content;
    i--;
  }
  return null;
}

type QuotedStringForward = {
  content: string;
  /** Index of the closing `'`. */
  closeIdx: number;
};

function readQuotedStringStartingAt(text: string, start: number): QuotedStringForward | null {
  if (text[start] !== "'") return null;
  let i = start + 1;
  let content = '';
  while (i < text.length) {
    const ch = text[i]!;
    if (ch === "'") {
      if (i + 1 < text.length && text[i + 1] === "'") {
        content += "'";
        i += 2;
        continue;
      }
      return { content, closeIdx: i };
    }
    if (ch === '\n') return null;
    content += ch;
    i++;
  }
  return null;
}
